/**
 * QLogo — 3D holographic frosted-glass Q for Qué Onda Chat branding.
 *
 *  - 100% SVG vector, transparent background.
 *  - Neon cyan #00f0ff core with rim highlights and inner frost.
 *  - Outer halo via CSS `drop-shadow` on the wrapper.
 *  - No solid rectangle behind the Q — the surrounding surface (dark slate)
 *    is expected to show through.
 */
export function QLogo({ size = 40, className = "", ariaLabel }) {
  return (
    <span
      className={"q-logo-wrap inline-flex items-center justify-center " + className}
      style={{
        width: size,
        height: size,
        // Outer holographic halo — brief requires this shadow spec.
        filter: "drop-shadow(0 0 10px rgba(0, 240, 255, 0.7))",
        overflow: "visible",
      }}
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      data-testid="q-logo"
    >
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block", overflow: "visible" }}
      >
        <defs>
          {/* Outer soft halo behind the Q body */}
          <radialGradient id="ql-halo" cx="50%" cy="50%" r="55%">
            <stop offset="20%" stopColor="#00f0ff" stopOpacity="0.55" />
            <stop offset="70%" stopColor="#00f0ff" stopOpacity="0.14" />
            <stop offset="100%" stopColor="#00f0ff" stopOpacity="0" />
          </radialGradient>
          {/* Ring stroke — top-left glass sheen bleeding into deep cyan */}
          <linearGradient id="ql-rim" x1="18%" y1="8%" x2="82%" y2="92%">
            <stop offset="0%" stopColor="#f4ffff" />
            <stop offset="42%" stopColor="#00f0ff" />
            <stop offset="100%" stopColor="#006680" />
          </linearGradient>
          {/* Frosted fill inside the ring */}
          <radialGradient id="ql-frost" cx="42%" cy="34%" r="70%">
            <stop offset="0%" stopColor="#c8fbff" stopOpacity="0.65" />
            <stop offset="55%" stopColor="#00f0ff" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#003a4a" stopOpacity="0.45" />
          </radialGradient>
        </defs>

        {/* Outer glow disc */}
        <circle cx="50" cy="50" r="46" fill="url(#ql-halo)" />

        {/* Q — outer ring stroked with rim gradient (frosted glass rim) */}
        <circle cx="50" cy="50" r="30" fill="url(#ql-frost)" />
        <circle cx="50" cy="50" r="30" fill="none" stroke="url(#ql-rim)" strokeWidth="7" />

        {/* Inner cutout ring (the counter of the Q) */}
        <circle cx="50" cy="50" r="19" fill="none" stroke="rgba(230, 255, 255, 0.55)" strokeWidth="1.2" />

        {/* Diagonal tail — extends from the inside of the ring outward */}
        <line
          x1="62"
          y1="62"
          x2="78"
          y2="78"
          stroke="url(#ql-rim)"
          strokeWidth="8"
          strokeLinecap="round"
        />
        {/* Inner cyan glow along the tail */}
        <line
          x1="63"
          y1="63"
          x2="77"
          y2="77"
          stroke="#e6ffff"
          strokeWidth="2.6"
          strokeLinecap="round"
          opacity="0.75"
        />

        {/* Top-left glass sheen highlight */}
        <path
          d="M 32 32 Q 44 24 58 25"
          fill="none"
          stroke="#ffffff"
          strokeWidth="1.9"
          strokeLinecap="round"
          opacity="0.7"
        />
        <circle cx="38" cy="34" r="1.6" fill="#ffffff" opacity="0.9" />
      </svg>
    </span>
  );
}

export default QLogo;
